<template lang="pug">
.c-setup(v-if="checklist && checklist.totalCount > 0")
  button.c-setup__reopen(v-if="checklist.isHidden" type="button" @click="setHidden(false)")
    fai(type="fal" icon="sparkles").me-2
    span {{ checklist.title }}
    span.c-setup__reopen-count {{ checklist.completedCount }}/{{ checklist.totalCount }}
    span.c-setup__reopen-action Show

  .c-setup__card(v-else)
    .c-setup__header
      .c-setup__heading
        .c-setup__title-row
          h3.c-setup__title {{ checklist.title }}
          span.c-setup__badge {{ checklist.completedCount }} of {{ checklist.totalCount }}
        p.c-setup__subtitle(v-if="checklist.subtitle") {{ checklist.subtitle }}
        .c-setup__progress
          .c-setup__progress-bar(:style="{ width: progressWidth }")
      button.c-setup__close(
        type="button" aria-label="Hide setup assistant" @click="setHidden(true)"
      )
        fai(type="fal" icon="times")

    .c-setup__complete(v-if="checklist.isComplete")
      fai(type="fal" icon="sparkles").c-setup__complete-icon
      div
        h4.c-setup__complete-title {{ checklist.completionTitle || 'All done!' }}
        p.c-setup__complete-text(v-if="checklist.completionMessage")
          | {{ checklist.completionMessage }}

    .c-setup__tasks(v-else)
      .c-setup__section(v-for="(group, index) in sections" :key="group.name || 'none'")
        h4.c-setup__section-title(v-if="group.name")
          fai(type="fal" icon="sparkles" :class="sectionIconClass(index)")
          | {{ group.name }}
        .c-setup__task(
          v-for="task in group.tasks"
          :key="task.id"
          :class="{ 'is-open': expandedTaskId === task.id && !task.isCompleted }"
        )
          button.c-setup__task-row(type="button" @click="toggleTask(task)")
            fai(v-if="task.isCompleted" type="fal" icon="check-circle").c-setup__check.is-done
            span.c-setup__check.is-empty(v-else)
            span.c-setup__task-title(:class="{ 'is-done': task.isCompleted }") {{ task.title }}
            fai(
              v-if="!task.isCompleted"
              type="fal"
              icon="chevron-right"
              :class="{ 'is-rotated': expandedTaskId === task.id }"
            ).c-setup__chevron
          .c-setup__task-body(v-if="expandedTaskId === task.id && !task.isCompleted")
            p.c-setup__tooltip(v-if="task.tooltip") {{ task.tooltip }}
            p.c-setup__details(v-if="task.details") {{ task.details }}
            img.c-setup__media(
              v-if="task.mediaUrl && isImage(task.mediaUrl)" :src="task.mediaUrl" alt=""
            )
            a.c-setup__media-link(
              v-else-if="task.mediaUrl" :href="task.mediaUrl" target="_blank" rel="noopener"
            ) Watch the explainer
            .c-setup__actions
              el-button(
                v-if="task.actionLabel && resolveAction(task)"
                type="primary"
                size="small"
                @click="runAction(task)"
              ) {{ task.actionLabel }}
              el-button(
                v-if="task.canMarkComplete"
                size="small"
                @click="markDone(task)"
              ) Mark as done
            help-question-box(
              :org-id="orgId" :task-id="task.id" placeholder="Still stuck on this step? Ask us"
            )
          .c-setup__task-undo(v-else-if="task.isCompleted && task.canMarkComplete")
            button.c-setup__undo(type="button" @click="markDone(task, false)") Undo

    .c-setup__footer
      .c-setup__tip(v-if="showProTip")
        fai(type="fal" icon="info-circle").c-setup__tip-icon
        p
          strong Pro Tip:
          |  {{ checklist.proTip }}
      el-button.c-setup__hide(@click="setHidden(true)") Hide setup assistant

  shop-embed-drawer(
    v-if="orgId"
    :model-value="sidebar === 'SHOP_EMBED'"
    :org-id="orgId"
    @update:model-value="sidebar = null"
  )
  el-dialog(
    :model-value="!!iframeUrl" width="80%" append-to-body @close="iframeUrl = null"
  )
    iframe.c-setup__iframe(v-if="iframeUrl" :src="iframeUrl" title="Setup")
  el-dialog(
    :model-value="!!deeplink" title="Open in the Spotlight app" width="380px"
    append-to-body @close="deeplink = null"
  )
    p This step is completed in the mobile app.
    a(:href="deeplink" target="_blank" rel="noopener") Open in the app
  el-dialog(
    :model-value="!!mobileStep" title="Do this in the Spotlight app" width="400px"
    append-to-body @close="mobileStep = null"
  )
    ol.c-setup-mobile__steps
      li Open the Spotlight app and sign in with this account.
      li Switch to your organization's profile.
      li {{ mobileStep }}
    p.c-setup-mobile__note This step is ticked automatically once it's done.
    .c-setup-mobile__stores
      a(:href="appStoreUrl" target="_blank" rel="noopener") App Store
      a(:href="playStoreUrl" target="_blank" rel="noopener") Google Play
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, onMounted, toRef,
} from 'vue';
import HelpQuestionBox from './HelpQuestionBox.vue';
import ShopEmbedDrawer from './ShopEmbedDrawer.vue';
import useSetupChecklist from './useSetupChecklist';

export default defineComponent({
  name: 'SetupChecklistPanel',
  components: { HelpQuestionBox, ShopEmbedDrawer },
  props: {
    placement: { type: String, required: true },
    orgId: { type: String, default: null },
  },
  setup(props) {
    const state = useSetupChecklist(props.placement, toRef(props, 'orgId'));
    const progressWidth = computed(() => {
      const c = state.checklist.value;
      return c && c.totalCount ? `${(c.completedCount / c.totalCount) * 100}%` : '0%';
    });
    // The tip is free text with no link to a task. When it names a step in quotes
    // (e.g. Complete "Add a Sticker" first) and that step is done, the advice is stale.
    const showProTip = computed(() => {
      const c = state.checklist.value;
      if (!c || !c.proTip || c.isComplete) return false;
      const quoted = (c.proTip.match(/["“][^"”]+["”]/g) || [])
        .map((q) => q.slice(1, -1).trim().toLowerCase());
      if (quoted.length === 0) return true;
      const named = c.tasks.filter((t) => quoted.includes(t.title.trim().toLowerCase()));
      return named.length === 0 || named.some((t) => !t.isCompleted);
    });
    const appStoreUrl = 'https://apps.apple.com/us/app/spotlight-xyz/id1566652200';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.spotlight.xyz';
    const isImage = (url: string) => /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url);
    onMounted(() => window.addEventListener('focus', state.onFocus));
    onBeforeUnmount(() => window.removeEventListener('focus', state.onFocus));
    const sectionIconClass = (index: number) => (index % 2 === 0 ? 'is-teal' : 'is-pink');
    return {
      ...state,
      progressWidth,
      showProTip,
      appStoreUrl,
      playStoreUrl,
      isImage,
      sectionIconClass,
    };
  },
});
</script>

<style lang="scss" scoped>
$ui-font: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue',
  Arial, sans-serif;

.c-setup {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 384px;
  max-width: calc(100vw - 32px);

  &__reopen {
    display: inline-flex;
    align-items: center;
    padding: 8px 14px;
    border: 1px solid #4349dd;
    border-radius: 999px;
    background: #e8e9fd;
    color: #4349dd;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(17, 24, 39, 0.18);
    &:hover { background: #dcdefb; }
  }
  &__reopen-count {
    margin-left: 8px;
    padding: 0 8px;
    border-radius: 999px;
    background: #fff;
    font-size: 12px;
  }
  &__reopen-action { margin-left: 12px; font-weight: 500; text-decoration: underline; }

  font-family: $ui-font;
  --el-font-family: #{$ui-font};

  :deep(svg) { width: 1em; height: 1em; }

  &__card {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: calc(100vh - 120px);
    // On phones keep part of the page visible behind the panel.
    @media (max-width: 767px) { max-height: 60vh; padding: 16px; }
    padding: 24px;
    border: 0;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  }
  &__header { display: flex; align-items: flex-start; flex: none; margin-bottom: 16px; }
  &__heading { flex: 1; }
  &__title-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
  &__title { margin: 0; font-size: 20px; line-height: 28px; font-weight: 700; color: #111827; }
  &__subtitle { margin: 0 0 8px; font-size: 14px; color: #4b5563; }
  &__badge {
    padding: 2px 8px;
    border: 1px solid #4349dd;
    border-radius: 999px;
    background: #e8e9fd;
    color: #4349dd;
    font-size: 12px;
  }
  &__progress { height: 4px; border-radius: 999px; background: #f3f4f6; overflow: hidden; }
  &__progress-bar {
    height: 100%;
    background: linear-gradient(to right, #01beb0, #4349dd);
    transition: width 0.5s;
  }
  &__close { margin-left: 12px; border: 0; background: none; color: #9ca3af; cursor: pointer; }

  &__tasks {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    margin-bottom: 16px;
  }
  &__section { margin-bottom: 16px; min-width: 0; &:last-child { margin-bottom: 0; } }
  &__section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
    padding: 0 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #111827;
  }
  &__section-title .is-teal { color: #01beb0; }
  &__section-title .is-pink { color: #ff6b9d; }
  &__task {
    margin-bottom: 4px;
    border-radius: 8px;
    overflow: hidden;
    &.is-open { background: #e8e9fd; }
  }
  &__task-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border: 0;
    background: none;
    text-align: left;
    cursor: pointer;
    &:hover { background: #f9fafb; }
  }
  .is-open &__task-row:hover { background: transparent; }
  &__check {
    flex: none;
    width: 20px;
    height: 20px;
    &.is-done { color: #01beb0; font-size: 20px; }
    &.is-empty { border: 2px solid #d1d5db; border-radius: 50%; }
  }
  &__task-title {
    flex: 1;
    font-size: 14px;
    color: #374151;
    &.is-done { color: #6b7280; text-decoration: line-through; }
  }
  .is-open &__task-title { color: #111827; font-weight: 600; }
  &__chevron {
    color: #9ca3af;
    transition: transform 0.2s;
    &.is-rotated { transform: rotate(90deg); }
  }
  &__task-body { padding: 4px 12px 12px; }
  &__tooltip, &__details { margin: 0 0 8px; font-size: 12px; line-height: 1.6; color: #374151; }
  &__details { font-style: italic; color: #4b5563; }
  &__media { max-width: 100%; border-radius: 6px; margin-bottom: 8px; }
  &__media-link { display: block; margin-bottom: 8px; font-size: 12px; }
  &__actions { display: flex; gap: 8px; margin-top: 8px; }
  &__task-undo { padding: 0 12px 8px 44px; }
  &__undo {
    border: 0;
    background: none;
    padding: 0;
    font-size: 12px;
    color: #6b7280;
    text-decoration: underline;
    cursor: pointer;
  }

  &__complete {
    display: flex;
    gap: 12px;
    padding: 24px;
    margin-bottom: 16px;
    border-radius: 8px;
    background: #c3eae3;
    color: #005c6b;
  }
  &__complete-title { margin: 0 0 4px; font-weight: 600; }
  &__complete-text { margin: 0; font-size: 14px; }

  &__footer {
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 0;
  }
  &__tip {
    display: flex;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: #fef7e0;
    font-size: 12px;
    color: #374151;
    p { margin: 0; }
    strong { color: #8a7800; }
  }
  &__tip-icon { margin-top: 2px; color: #8a7800; }
  &__hide.el-button {
    width: 100%;
    height: 36px;
    border: 0;
    border-radius: 8px;
    background: #01beb0;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    &:hover { background: #005c6b; color: #fff; }
  }
  &__actions :deep(.el-button--primary) {
    border: 0;
    background: #01beb0;
    font-size: 12px;
    &:hover { background: #005c6b; }
  }
  &__iframe { width: 100%; height: 70vh; border: 0; }
}

// Rendered in a dialog outside .c-setup (append-to-body).
.c-setup-mobile {
  &__steps {
    margin: 0 0 12px;
    padding-left: 20px;
    line-height: 1.8;
    color: #374151;
    word-break: normal;
  }
  &__note { margin: 0 0 16px; font-size: 12px; color: #6b7280; }
  &__stores { display: flex; gap: 16px; font-weight: 500; }
}
</style>
